import { usePage } from '@inertiajs/react';
import { type ReactNode, useEffect } from 'react';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';

// The persistent shell of every backoffice page (Next: app/admin/(protected)/layout.tsx).
// It takes only `children` and reads its data from the shared props — an Inertia layout
// function is called twice and the first call gets the raw page props, so anything it
// destructures from an argument blows up.
export function AdminLayout({ children }: { children: ReactNode }) {
  const { adminShell, auth } = usePage().props;

  // The Next admin route group loads its own stylesheet, which repaints the whole
  // document; here the palette is a class (`.theme-admin` in stylesheets/application.css)
  // and it has to sit on <body> so the portalled dialogs, dropdowns and tooltips get it
  // too. The server puts it there for the first paint; this keeps it right when Inertia
  // navigates in and out of the backoffice.
  useEffect(() => {
    document.body.classList.add('theme-admin');

    return () => document.body.classList.remove('theme-admin');
  }, []);

  if (!adminShell || !auth.user) {
    return <>{children}</>;
  }

  return (
    <SidebarProvider>
      <AdminSidebar
        events={adminShell.events}
        user={auth.user}
        defaultEventSlug={adminShell.defaultEventSlug}
        activeEventSlug={adminShell.activeEventSlug}
      />
      <SidebarInset>
        <ImpersonationBanner
          impersonatedBy={adminShell.impersonatedBy}
          userName={auth.user.name}
          userEmail={auth.user.email}
        />
        {/* AdminSidebar's own trigger lives inside the sidebar, which on mobile is an
            off-canvas Sheet closed by default — so it's unreachable until it's already
            open. This bar is the only way a mobile visitor can open it. Sticky (not
            static) so it stays reachable while scrolling down a long page — the content
            below scrolls with the document, main has no fixed height/overflow of its own. */}
        <header className="sticky top-0 z-30 flex items-center gap-2 border-b bg-background p-3 md:hidden">
          <SidebarTrigger />
          <span className="font-semibold text-sm">Platanus Hack Admin</span>
        </header>
        <div className="min-w-0 flex-1 overflow-auto p-4 md:p-6">
          {children}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
